// The screens' only route to the API: turns thrown ApiErrors into results the UI can render.

import type { FieldError, Order, OrderForm } from '@hearingaid/shared'
import { getClient } from '../api/client'
import type { OrderPage, OrderSearch } from '../api/contract'
import { isApiError } from '../api/errors'

export type SubmitResult =
  | { ok: true; orderId: string }
  | { ok: false; errors: FieldError[] }
  | { ok: false; sessionExpired: true }
  | { ok: false; rejected: true; message: string }
  | { ok: false; saveFailed: true }

/**
 * 422 with field errors -> `errors` to show. 401 -> `sessionExpired` (form kept; the screen
 * offers re-sign-in). Any other 4xx (400, 403, 409, 422 without field errors) -> `rejected`
 * with the API's message: retrying the same key can never succeed. Network errors, 5xx, and
 * 429 (throttled by the gateway or the authorizer) -> `saveFailed`: the caller keeps the form
 * and its submission_key, so a retry replays the order.
 */
export async function submit(form: OrderForm): Promise<SubmitResult> {
  try {
    const { order } = await getClient().submitOrder(form)
    return { ok: true, orderId: order.id }
  } catch (e) {
    if (!isApiError(e) || e.status >= 500 || e.status === 429) return { ok: false, saveFailed: true }
    if (e.status === 401) return { ok: false, sessionExpired: true }
    if (e.status === 422 && e.fieldErrors.length > 0) return { ok: false, errors: e.fieldErrors }
    if (e.status >= 400) return { ok: false, rejected: true, message: e.message }
    return { ok: false, saveFailed: true }
  }
}

export type HistoryFilters = Omit<OrderSearch, 'cursor'>

export function loadHistory(filters: HistoryFilters, cursor?: string): Promise<OrderPage> {
  return getClient().searchOrders({ ...filters, cursor })
}

export function loadOrder(id: string): Promise<{ order: Order; email_to: string }> {
  return getClient().getOrder(id)
}

export type OrderLookup =
  | { status: 'found'; order: Order; emailTo: string }
  | { status: 'notFound' }
  | { status: 'failed' }

/** `loadOrder` for the detail screen: a 404 (also the API's answer to a malformed id) is "not found"; anything else "failed". */
export async function findOrder(id: string): Promise<OrderLookup> {
  try {
    const { order, email_to } = await loadOrder(id)
    return { status: 'found', order, emailTo: email_to }
  } catch (e) {
    if (isApiError(e) && e.status === 404) return { status: 'notFound' }
    return { status: 'failed' }
  }
}
